import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, ArrowRight, Sparkles, Clock, Tag, Plus } from "lucide-react";
import { useNavigate } from 'react-router';
import { useServiceSuggestions } from "@/hooks/useServiceSuggestions";
import { useTaskSuggestions } from "@/hooks/useTaskSuggestions";
import { useDynamicCategories } from "@/hooks/useDynamicCategories";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { useCountryConfig } from "@/lib/countryConfig";
import { useToast } from "@/hooks/use-toast";
import { SERVICE_SUGGESTION_ROUTES } from "@/lib/serviceRoutes";
import { AiProviderNotice } from "@/components/AiProviderNotice";

interface SearchDialogProps {
  children: React.ReactNode;
}

export function SearchDialog({ children }: SearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { cities } = useCountryConfig();
  const { toast } = useToast();
  const { categories } = useDynamicCategories();
  const { suggestions: serviceSuggestions, isLoading: isLoadingService, getSuggestions: getServiceSuggestions, clearSuggestions: clearServiceSuggestions } = useServiceSuggestions();
  const { suggestions: taskSuggestions, isLoading: isLoadingTask, getSuggestions: getTaskSuggestions, clearSuggestions: clearTaskSuggestions } = useTaskSuggestions();

  // Debounce AI suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 5) {
        getTaskSuggestions(searchQuery);
        getServiceSuggestions(searchQuery);
      } else {
        clearTaskSuggestions();
        clearServiceSuggestions();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Every entry point leads to the one booking process, the wizard (owner
  // rule, 2026-08-14) — helpers only appear at step 4, in the context of an
  // active booking, not on a separate browse page first. Unresolved free
  // text still lands in the wizard; BookingSearch's own gate (the
  // root-cause fix for the fictitious-category bug) then shows an honest
  // rejection and reopens the real category grid.
  const handleSearch = (queryOverride?: string) => {
    setOpen(false);
    const query = queryOverride ?? searchQuery;
    const params = new URLSearchParams();
    if (query) {
      params.set('task', query);
      params.set('category', query);
    }
    if (location) params.set('address', location);
    const q = params.toString();
    navigate(q ? `/booking-flow?${q}` : '/booking-flow');
  };

  const handleTaskSuggestionClick = (suggestion: typeof taskSuggestions[0]) => {
    // suggest-task-options returns a category SLUG from a static prompt list
    // that can drift from the live catalogue, while BookingSearch.tsx's own
    // gate matches by category NAME — resolve against the live catalogue by
    // slug or name before navigating, instead of relying solely on that
    // downstream gate to catch it.
    const match = categories.find(
      (c) =>
        c.slug.trim().toLowerCase() === suggestion.category.trim().toLowerCase() ||
        c.name.trim().toLowerCase() === suggestion.category.trim().toLowerCase(),
    );
    if (!match) {
      toast({
        title: "We couldn't match that suggestion to a service",
        description: 'Try a different search, or browse the categories below.',
        variant: 'destructive',
      });
      return;
    }
    setOpen(false);
    navigate(`/booking-flow?task=${encodeURIComponent(suggestion.description)}&category=${encodeURIComponent(match.name)}`);
  };

  const handleServiceSuggestionClick = (serviceId: string) => {
    setOpen(false);
    navigate(SERVICE_SUGGESTION_ROUTES[serviceId] || '/services/all');
  };

  return (
    <>
      {/* Trigger — click bubbles to open the modal */}
      <div style={{ display: "contents" }} onClick={() => setOpen(true)}>
        {children}
      </div>

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title={
          <span className="flex items-center gap-2">
            <Search className="h-5 w-5" aria-hidden="true" />
            Find Services
          </span>
        }
        description="Search Kanfy for helpers in your area"
        footer={
          <Button onClick={() => handleSearch()} className="w-full">
            Search Services
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        }
      >
        <div className="space-y-4">
          <Input
              type="search"
              label="What service do you need?"
              placeholder="Describe what you need (e.g., someone to clean my house weekly)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 min-w-0 bg-background"
              leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              aria-label="Search for services"
              list="services-list"
            />
          <AiProviderNotice />
          <datalist id="services-list"><option value="Cleaning" /><option value="Childcare" /><option value="Elderly Care" /><option value="Gardening" /><option value="Security" /><option value="Tutoring" /><option value="Pet Care" /><option value="Home Maintenance" /><option value="Deep Cleaning" /><option value="Regular House Cleaning" /><option value="Office Cleaning" /><option value="Move In/Out Cleaning" /><option value="Window Cleaning" /><option value="Laundry Service" /><option value="Carpet Cleaning" /></datalist>
            {(isLoadingTask || isLoadingService) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" aria-live="polite">
                <Sparkles className="w-4 h-4 text-primary animate-pulse" aria-hidden="true" />
                <span>Finding suggestions...</span>
              </div>
            )}

          {/* AI Task Suggestions */}
          {taskSuggestions.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI Suggested Tasks:
              </p>
              {taskSuggestions.map((suggestion, index) => (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
                  onClick={() => handleTaskSuggestionClick(suggestion)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-sm text-foreground">
                            {suggestion.description}
                          </h4>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Tag className="w-3 h-3" />
                            <span>{suggestion.category}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{suggestion.estimatedDuration}h</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {suggestion.details}
                        </p>
                      </div>
                      <Plus className="h-4 w-4 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* AI Service Suggestions */}
          {serviceSuggestions.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Related Services:
              </p>
              {serviceSuggestions.map((suggestion, index) => (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
                  onClick={() => handleServiceSuggestionClick(suggestion.serviceId)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-sm text-foreground truncate">
                            {suggestion.serviceName}
                          </h4>
                          <Badge
                            variant={suggestion.confidence === 'high' ? 'default' : 'secondary'}
                            className="text-xs shrink-0"
                          >
                            {suggestion.confidence === 'high' ? 'Best match' : 'Match'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {suggestion.reason}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Input
              type="text"
              label="Location"
              placeholder="Enter your location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-12 min-w-0 bg-background"
              leftIcon={<MapPin className="h-4 w-4" aria-hidden="true" />}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              aria-label="Enter your location"
              list="locations-list"
            />
          <datalist id="locations-list">{cities.map((city) => <option key={city} value={city} />)}</datalist>

          {/* Popular services chips */}
          <div className="pt-2 border-t">
            <p className="text-sm text-muted-foreground mb-3">Popular services:</p>
            <div className="flex flex-wrap gap-2">
              {['House Cleaning', 'Maid Service', 'Security', 'Maintenance'].map((service) => (
                <Button key={service} variant="outline" size="sm" onClick={() => handleSearch(service)}>
                  {service}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
